// Periodic B2B token validator.
// Boot flow authenticates once; this keeps checking the session (user/checkB2bToken:
// the proxy token every minute, a new /b2b/authenticate every 10 minutes) so an
// operator-token invalidation on the B2B side kicks the user out promptly.
// Singleton state — one watcher per app instance.

let intervalId               = null;
let inFlight                 = false;
let consecutiveTransientFails = 0;
let ticksToSkip              = 0;

const DEFAULT_INTERVAL_MS         = 60 * 1000;
const DEFAULT_TRANSIENT_THRESHOLD = 3; // 3 failed checks in a row (with the backoff, ~7 minutes) before we give up
// Backoff after transient failures: the next check waits 1, 3, 7… ticks more (2^n - 1), capped,
// so an unreachable proxy is not hit every minute.
const MAX_BACKOFF_TICKS           = 15;

export function backoffTicks(consecutiveFails) {
    return Math.min(Math.pow(2, consecutiveFails) - 1, MAX_BACKOFF_TICKS);
}

// Start polling. Safe to call multiple times: extra calls are ignored.
// - store:               Vuex store
// - intervalMs:          poll period (default 60s)
// - transientThreshold:  how many network/5xx errors in a row before we treat
//                        the session as lost (default 3). A user on a flaky
//                        connection should not be booted after a single
//                        timeout.
// - onInvalid:           invoked with { code } when the token is rejected
//                        (either an explicit backend rejection OR the transient
//                        threshold was crossed). The watcher auto-stops before
//                        invoking to avoid noise.
export function startB2bTokenWatcher(store, {
    intervalMs         = DEFAULT_INTERVAL_MS,
    transientThreshold = DEFAULT_TRANSIENT_THRESHOLD,
    onInvalid
} = {}) {
    if (intervalId) return;
    consecutiveTransientFails = 0;
    ticksToSkip = 0;

    // A failure worth retrying: count it, and wait longer before the next check
    const transientFailure = (code) => {
        consecutiveTransientFails += 1;
        ticksToSkip = backoffTicks(consecutiveTransientFails);
        console.warn(
            `[B2B watcher] Transient failure ${consecutiveTransientFails}/${transientThreshold}`,
            'code:', code, `- next check in ${ticksToSkip + 1} tick(s)`
        );
        return consecutiveTransientFails >= transientThreshold;
    };

    intervalId = setInterval(async () => {
        if (inFlight) return;
        if (ticksToSkip > 0) {
            ticksToSkip -= 1;
            return;
        }
        if (!store.getters['settings/getB2bEnabled']) return;
        if (!store.getters['user/getB2bToken']) return;

        inFlight = true;
        try {
            const result = await store.dispatch('user/checkB2bToken');

            if (!result || result.valid !== false) {
                // Success: reset the transient counter and the backoff.
                consecutiveTransientFails = 0;
                ticksToSkip = 0;
                return;
            }

            // Below the threshold → retry later; crossed → treat as invalid session.
            if (result.transient && !transientFailure(result.code)) return;

            stopB2bTokenWatcher();
            if (typeof onInvalid === 'function') onInvalid(result);
        } catch (err) {
            // A bug in the check must not turn into a call every minute either
            console.error('[B2B watcher] Unexpected error during check:', err);
            if (transientFailure(0)) {
                stopB2bTokenWatcher();
                if (typeof onInvalid === 'function') onInvalid({valid: false, code: 0, transient: true});
            }
        } finally {
            inFlight = false;
        }
    }, intervalMs);
}

export function stopB2bTokenWatcher() {
    if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
    }
    consecutiveTransientFails = 0;
    ticksToSkip = 0;
}
